import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("This reset link is missing its token. Please request a new one.");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("The two passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      toast.success(data.message || "Password updated.");
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-16"
      data-testid="reset-password-page"
    >
      <div className="max-w-md w-full">
        <div className="flex justify-center mb-8">
          <BrandLogo height={64} linkTo="/" darkenGrey />
        </div>
        <div className="ser-card p-8 md:p-10">
          <p className="eyebrow mb-3">Set a new password</p>
          <h1
            className="font-serif-display text-3xl mb-3"
            style={{ color: "var(--ser-primary-dark)" }}
          >
            Choose a new password
          </h1>

          {done ? (
            <div
              className="rounded border p-5 flex items-start gap-3"
              style={{
                background: "var(--ser-surface)",
                borderColor: "var(--ser-sand-dark)",
              }}
              data-testid="reset-password-success"
            >
              <CheckCircle2
                className="w-5 h-5 mt-0.5 shrink-0"
                style={{ color: "var(--ser-primary)" }}
              />
              <div className="text-sm" style={{ color: "var(--ser-text)" }}>
                Your password has been reset. Redirecting you to sign in…
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm mb-6" style={{ color: "var(--ser-text-muted)" }}>
                Enter a new password below. Reset links are valid for 30 minutes
                and can only be used once.
              </p>
              <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
                <div>
                  <label className="ser-label">New password</label>
                  <input
                    required
                    type="password"
                    minLength={6}
                    className="ser-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-new"
                  />
                </div>
                <div>
                  <label className="ser-label">Confirm new password</label>
                  <input
                    required
                    type="password"
                    minLength={6}
                    className="ser-input"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-password-confirm"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !token}
                  className="btn-ser-primary w-full"
                  data-testid="reset-password-submit-btn"
                >
                  <ShieldCheck className="w-4 h-4" />
                  {loading ? "Updating…" : "Update password"}
                </button>
              </form>
            </>
          )}

          <p className="text-center text-sm mt-6" style={{ color: "var(--ser-text-muted)" }}>
            Remembered it?{" "}
            <Link
              to="/login"
              className="font-semibold hover:underline"
              style={{ color: "var(--ser-primary)" }}
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
